import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Check, Shield, FileText, Download, Clock, Users, Phone, Mail, MapPin } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_estate-planner-16/artifacts/7xqxcajt_LOGO.jpg';

export const LandingPage = () => {
  const features = [
    {
      icon: <Shield className="w-8 h-8 text-[#C5A059]" />,
      title: 'Legally Valid',
      description: 'Documents approved by the Law Society of Ireland'
    },
    {
      icon: <FileText className="w-8 h-8 text-[#C5A059]" />,
      title: 'Step-by-Step Guidance',
      description: 'Easy-to-follow questionnaire walks you through every section'
    },
    {
      icon: <Download className="w-8 h-8 text-[#C5A059]" />,
      title: 'PDF Download',
      description: 'Download your completed will and questionnaire instantly'
    },
    {
      icon: <Clock className="w-8 h-8 text-[#C5A059]" />,
      title: 'Save Progress',
      description: 'Complete at your own pace - your progress is automatically saved'
    }
  ];

  const pricingPlans = [
    {
      title: 'Individual Will',
      price: '€150',
      description: 'Perfect for single individuals',
      features: [
        'Step-by-step guidance',
        'Legally valid in Ireland',
        'PDF download',
        'Progress saving',
        'Email support'
      ],
      popular: false
    },
    {
      title: 'Mirror Wills',
      price: '€250',
      description: 'For couples with matching provisions',
      features: [
        'Two complete wills',
        'Matching provisions',
        'Spouse-to-spouse coverage',
        'PDF downloads for both',
        'Best value for couples',
        'Priority support'
      ],
      popular: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src={LOGO_URL} alt="Clevermoney" className="h-10 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-[#0A192F] hover:text-[#C5A059]" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#0A192F] hover:bg-[#172A46] text-white" data-testid="nav-register-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-bg py-20 lg:py-32" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6 animate-fade-in">
              Secure Your Legacy <br />
              <span className="text-[#C5A059]">with Confidence</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8 animate-slide-up">
              Professional Irish Will Writing Service. Legally valid, easy to complete, 
              and backed by financial experts at Clevermoney.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
              <Link to="/register">
                <Button className="btn-gold text-lg px-8 py-4" data-testid="hero-cta-btn">
                  Start Your Will Today
                </Button>
              </Link>
              <a href="#pricing">
                <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 text-lg px-8 py-4">
                  View Pricing
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-white py-8 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 text-[#475569]">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#10B981]" />
              <span className="text-sm font-medium">Law Society Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#10B981]" />
              <span className="text-sm font-medium">Regulated by Central Bank of Ireland</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#10B981]" />
              <span className="text-sm font-medium">Trusted Financial Advisors</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#FDFBF7]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0A192F] mb-4">
              Why Choose Our Will Service?
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              We've made will writing simple, secure, and affordable for all our clients.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="card-feature text-center" data-testid={`feature-card-${index}`}>
                <CardContent className="pt-8">
                  <div className="flex justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-heading text-xl text-[#0A192F] mb-2">{feature.title}</h3>
                  <p className="text-[#475569] text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0A192F] mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              No hidden fees. Choose the plan that suits your needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`card-pricing ${plan.popular ? 'border-[#C5A059] border-2' : ''}`}
                data-testid={`pricing-card-${index}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-[#C5A059] text-white px-4 py-1 text-sm font-medium rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="font-heading text-2xl text-[#0A192F]">{plan.title}</CardTitle>
                  <CardDescription className="text-[#475569]">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-[#0A192F]">{plan.price}</span>
                  </div>
                  <ul className="space-y-3 mb-8 text-left">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                        <span className="text-[#475569]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/register">
                    <Button 
                      className={plan.popular ? 'btn-gold w-full' : 'btn-primary w-full'}
                      data-testid={`pricing-cta-${index}`}
                    >
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#0A192F]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
              How It Works
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Complete your will in just a few simple steps
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Create Account', desc: 'Register with your email' },
              { step: '2', title: 'Fill Questionnaire', desc: 'Answer simple questions about your wishes' },
              { step: '3', title: 'Review Your Will', desc: 'We generate the right will type for you' },
              { step: '4', title: 'Download & Sign', desc: 'Get your will witnessed to make it valid' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#C5A059] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="font-heading text-xl text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Notice */}
      <section className="py-12 bg-[#FDFBF7] border-t border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-6 rounded-xl border border-[#E2E8F0]">
            <h3 className="font-heading text-lg text-[#0A192F] mb-3">Important Notice</h3>
            <p className="text-sm text-[#475569] font-legal leading-relaxed">
              The provision of this product or service does not require licensing, authorization, 
              or registration with the Central Bank of Ireland and, as a result, it is not covered 
              by Central Bank consumer protection requirements or a statutory scheme. 
              Charterhouse Private Clients Ltd trading as Clevermoney is regulated by the 
              Central Bank of Ireland.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A192F] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src={LOGO_URL} alt="Clevermoney" className="h-10 w-auto mb-4 brightness-0 invert" />
              <p className="text-gray-400 text-sm">
                Professional financial services and will writing for Irish clients.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Us</h4>
              <div className="space-y-2">
                <a href="mailto:info@clevermoney.ie" className="flex items-center gap-2 text-gray-400 hover:text-[#C5A059] text-sm">
                  <Mail className="w-4 h-4" />
                  info@clevermoney.ie
                </a>
                <a href="https://clevermoney.ie" className="flex items-center gap-2 text-gray-400 hover:text-[#C5A059] text-sm">
                  <MapPin className="w-4 h-4" />
                  www.clevermoney.ie
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/login" className="block text-gray-400 hover:text-[#C5A059] text-sm">Sign In</Link>
                <Link to="/register" className="block text-gray-400 hover:text-[#C5A059] text-sm">Get Started</Link>
                <a href="#pricing" className="block text-gray-400 hover:text-[#C5A059] text-sm">Pricing</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Clevermoney. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
